/**
 * ruleset — a compact human label of a level's EFFECTIVE RULE SET, so a player knows what
 * they're playing BEFORE they start it (Isaac 2026-07-01: "the platform/type/variant should be
 * labeled on every single level that gets published"). Composes the movement ARCHETYPE (the
 * feel model — Classic/Precision/Combat/Dash/Momentum/Auto-Run), the WIN condition (when it's
 * not the default reach-the-goal), a countdown timer, and any author-suggested mutator category.
 *
 * Pure + deterministic-lint-clean (string building only): reusable by the menu cards, the
 * community browse list, the editor, and the in-game chip from a bare `LevelMeta` — no World.
 */
import { resolveArchetype } from './Archetype';
import { resolveMutators } from './Mutators';
import type { ClearCondition, LevelMeta } from './LevelFormat';

/** The static RULE name for a clear condition (no live progress — for a card/label). */
export function conditionRuleName(c: ClearCondition | undefined): string {
  switch (c?.kind) {
    case 'collect_all_coins': return 'Collect all coins';
    case 'defeat_all_enemies': return 'Defeat all enemies';
    case 'reach_goal_with_coins': return `Reach goal + ${c.n ?? 0} coins`;
    default: return 'Reach goal';
  }
}

/** The fields a ruleset label reads — a subset of LevelMeta so a LevelSummary can supply them. */
export type RulesetFields = Pick<LevelMeta, 'archetype' | 'clearCondition' | 'timeLimitSec' | 'suggestedMutators'>;

/**
 * A ` · `-joined ruleset label, e.g. `Precision · Collect all coins · ⏱ 60s` or just `Classic`.
 * The archetype is ALWAYS shown (it IS the core "what am I playing"); the condition/timer/mutators
 * are added only when non-default so a plain classic reach-goal level reads simply `Classic`.
 */
export function rulesetLabel(meta: RulesetFields): string {
  const parts: string[] = [resolveArchetype(meta.archetype).label];
  if (meta.clearCondition && meta.clearCondition.kind !== 'reach_goal') {
    parts.push(conditionRuleName(meta.clearCondition));
  }
  if (meta.timeLimitSec && meta.timeLimitSec > 0) parts.push(`⏱ ${meta.timeLimitSec}s`);
  const muts = resolveMutators(meta.suggestedMutators ?? []);
  if (muts.setId !== 0) parts.push(muts.label());
  return parts.join(' · ');
}
